import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Icon } from '@iconify/react';
import CONFIG from '../../config';
import { useSimulation } from '../../contexts/SimulationContext';
import CandidateDetailDrawer from './CandidateDetailDrawer';
import LoadingSpinner from '../common/LoadingSpinner';

const Candidates = () => {
    const { project, setProject, candidates: contextCandidates, loading } = useSimulation();
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStage, setFilterStage] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterBatch, setFilterBatch] = useState('all');
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [page, setPage] = useState(1);
    const itemsPerPage = 10;

    // Use candidates from context for better reliability
    const candidates = contextCandidates || [];

    const batches = ['all', ...new Set(candidates.map(c => c.batch_id).filter(Boolean))];
    const stages = ['all', ...new Set(candidates.map(c => c.current_stage).filter(Boolean))];

    const filteredCandidates = candidates.filter(c => {
        const matchesSearch =
            c.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            c.unique_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            c.chest_number?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStage = filterStage === 'all' || c.current_stage === filterStage;
        const matchesBatch = filterBatch === 'all' || c.batch_id === filterBatch;
        const matchesStatus = filterStatus === 'all' || c.status === filterStatus;
        
        return matchesSearch && matchesStage && matchesBatch && matchesStatus;
    });

    const totalPages = Math.ceil(filteredCandidates.length / itemsPerPage);
    const paginatedCandidates = filteredCandidates.slice((page - 1) * itemsPerPage, page * itemsPerPage);

    const handleRowClick = (cand) => {
        setSelectedCandidate(cand);
        setIsDrawerOpen(true);
    };

    const handleRefresh = () => {
        if (project) {
            // This triggers a refresh in SimulationContext
            setProject(project, true);
        }
    };

    return (
        <div className="tab-pane-content" style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            animation: 'fadeIn 0.5s ease-out',
            padding: '32px 40px',
            paddingBottom: '120px',
            width: '100%',
            boxSizing: 'border-box'
        }}>
            {/* Redesigned Content Table Container */}
            <div className="glass-card" style={{ padding: '32px', marginBottom: '40px', width: '100%', boxSizing: 'border-box' }}>
                {/* Row 1: Title Section */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div style={{ padding: '12px', background: 'rgba(128,0,0,0.1)', color: 'var(--primary)', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Icon icon="solar:users-group-rounded-bold-duotone" width="28" />
                        </div>
                        <div>
                            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 900, letterSpacing: '-0.5px' }}>CANDIDATE REPOSITORY</h2>
                            <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', opacity: 0.8 }}>Manage and track all project applicants</p>
                        </div>
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: 700, background: '#f8fafc', padding: '8px 16px', borderRadius: '10px', border: '1px solid var(--border-light)' }}>
                        TOTAL RECORDED: <span style={{ color: 'var(--primary)', fontWeight: 900 }}>{candidates.length}</span>
                    </div>
                </div>

                {/* Row 2: Full-Width Filter Bar (Single Row) */}
                <div style={{ 
                    display: 'flex', 
                    gap: '12px', 
                    width: '100%', 
                    alignItems: 'center', 
                    marginBottom: '32px',
                    background: '#f8fafc',
                    padding: '12px',
                    borderRadius: '16px',
                    border: '1px solid var(--border-light)',
                    boxSizing: 'border-box'
                }}>
                    {/* Search Field - Takes more space */}
                    <div style={{ position: 'relative', flex: 3, minWidth: '200px' }}>
                        <Icon icon="solar:magnifer-linear" width="18" style={{ position: 'absolute', left: '14px', top: '12px', color: 'var(--text-muted)' }} />
                        <input
                            type="text"
                            placeholder="Search by ID, Chest #, or Name..."
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                            className="input-field"
                            style={{ width: '100%', padding: '12px 12px 12px 42px', fontSize: '14px', fontWeight: 600, border: 'none', background: 'white' }}
                        />
                    </div>

                    {/* Batch Filter */}
                    <div style={{ flex: 1, minWidth: '120px' }}>
                        <select
                            value={filterBatch}
                            onChange={(e) => { setFilterBatch(e.target.value); setPage(1); }}
                            className="input-field"
                            style={{ width: '100%', padding: '10px 14px', fontWeight: 700, fontSize: '13px', border: 'none', background: 'white' }}
                        >
                            <option value="all">Every Batch</option>
                            {batches.filter(b => b !== 'all').map(b => (
                                <option key={b} value={b}>Batch {b}</option>
                            ))}
                        </select>
                    </div>

                    {/* Stage Filter */}
                    <div style={{ flex: 1, minWidth: '120px' }}>
                        <select
                            value={filterStage}
                            onChange={(e) => { setFilterStage(e.target.value); setPage(1); }}
                            className="input-field"
                            style={{ width: '100%', padding: '10px 14px', fontWeight: 700, fontSize: '13px', border: 'none', background: 'white' }}
                        >
                            <option value="all">All Stages</option>
                            {stages.filter(s => s !== 'all').map(s => (
                                <option key={s} value={s}>{s.replace('_', ' ')}</option>
                            ))}
                        </select>
                    </div>

                    {/* Status Filter - Expanded List */}
                    <div style={{ flex: 1, minWidth: '140px' }}>
                        <select
                            value={filterStatus}
                            onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
                            className="input-field"
                            style={{ width: '100%', padding: '10px 14px', fontWeight: 700, fontSize: '13px', border: 'none', background: 'white' }}
                        >
                            <option value="all">All Statuses</option>
                            <option value="PENDING">PENDING</option>
                            <option value="IN_PROGRESS">IN PROGRESS</option>
                            <option value="COMPLETED">COMPLETED / CLEARED</option>
                            <option value="DISCONTINUED">DISCONTINUED / REJECTED</option>
                            <option value="REJECTED">LEGACY REJECTED</option>
                        </select>
                    </div>

                    {/* Refresh Button */}
                    <button
                        onClick={handleRefresh}
                        className="btn-secondary"
                        style={{ 
                            padding: '0', 
                            width: '44px', 
                            height: '44px', 
                            borderRadius: '12px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            flexShrink: 0,
                            background: 'white',
                            border: 'none',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                        }}
                        title="Force Refresh Data"
                    >
                        <Icon icon="solar:refresh-bold-duotone" width="22" />
                    </button>
                </div>

                <div style={{ minHeight: '520px', position: 'relative', background: '#fff', borderRadius: '16px', border: '1px solid var(--border-light)', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ textAlign: 'left', background: '#f8fafc', borderBottom: '2px solid var(--border-light)' }}>
                                <th style={{ padding: '18px 24px', color: 'var(--text-muted)', fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>CANDIDATE & ID</th>
                                <th style={{ padding: '18px 24px', color: 'var(--text-muted)', fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>CHEST & BATCH</th>
                                <th style={{ padding: '18px 24px', color: 'var(--text-muted)', fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>STAGE & POSITION</th>
                                <th style={{ padding: '18px 24px', color: 'var(--text-muted)', fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>STATUS</th>
                                <th style={{ padding: '18px 24px', color: 'var(--text-muted)', fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px', textAlign: 'right' }}>ACTION</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="5" style={{ textAlign: 'center', padding: '140px 0' }}>
                                        <LoadingSpinner message="Syncing Repository..." />
                                    </td>
                                </tr>
                             ) : filteredCandidates.length === 0 ? (
                                <tr>
                                    <td colSpan="5" style={{ padding: '120px 0', textAlign: 'center' }}>
                                        <div className="animate-scale-in" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
                                            <div style={{
                                                width: '100px',
                                                height: '100px',
                                                borderRadius: '30px',
                                                background: 'rgba(128, 0, 0, 0.04)',
                                                border: '1px solid rgba(128, 0, 0, 0.08)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                position: 'relative'
                                            }}>
                                                <Icon 
                                                    icon={candidates.length === 0 ? "solar:user-block-bold-duotone" : "solar:filter-bold-duotone"} 
                                                    width="48" 
                                                    color="var(--primary)" 
                                                    style={{ opacity: 0.8 }} 
                                                />
                                                <div style={{ position: 'absolute', bottom: '-5px', right: '-5px', background: 'var(--primary)', color: 'white', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '3px solid white' }}>
                                                    <Icon icon="solar:close-circle-bold" width="14" />
                                                </div>
                                            </div>
                                             <div style={{ maxWidth: '340px' }}>
                                                <h4 style={{ fontSize: '20px', fontWeight: 900, color: 'var(--text-main)', marginBottom: '8px', letterSpacing: '-0.5px' }}>
                                                    {candidates.length === 0 ? 'No Candidates Onboarded' : 'No Matching Candidates'}
                                                </h4>
                                                <p style={{ fontSize: '14px', color: 'var(--text-muted)', lineHeight: '1.6', fontWeight: 600 }}>
                                                    {candidates.length === 0 
                                                        ? 'This project repository is currently empty. Please import or add candidates to start tracking.' 
                                                        : 'We found no records matching your current filter criteria. Try adjusting your search or selection.'}
                                                </p>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                paginatedCandidates.map(cand => (
                                    <tr key={cand._id} className="table-row-hover" onClick={() => handleRowClick(cand)} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}>
                                        <td style={{ padding: '16px 24px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(128,0,0,0.05)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 }}>
                                                    {cand.full_name?.charAt(0)}
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--text-dark)' }}>{cand.full_name}</div>
                                                    <div style={{ fontSize: '11px', color: 'var(--primary)', fontWeight: 800, opacity: 0.7 }}>{cand.unique_id}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td style={{ padding: '16px 24px' }}>
                                            <div style={{ fontWeight: 800, color: 'var(--text-main)', fontSize: '14px' }}>CHEST: {cand.chest_number || '---'}</div>
                                            <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600 }}>BATCH {cand.batch_id || 'N/A'}</div>
                                        </td>
                                        <td style={{ padding: '16px 24px' }}>
                                            <div style={{ fontWeight: 700, fontSize: '13px', color: '#0369a1' }}>{cand.current_stage}</div>
                                            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>CURRENT PIPELINE POSITION</div>
                                        </td>
                                        <td style={{ padding: '16px 24px' }}>
                                            <span style={{
                                                fontSize: '10px',
                                                fontWeight: 800,
                                                padding: '4px 12px',
                                                borderRadius: '20px',
                                                background: cand.status === 'DISCONTINUED' ? '#fee2e2' : cand.status === 'COMPLETED' ? '#dcfce7' : '#fef3c7',
                                                color: cand.status === 'DISCONTINUED' ? '#991b1b' : cand.status === 'COMPLETED' ? '#166534' : '#92400e',
                                                letterSpacing: '0.5px',
                                                border: `1px solid ${cand.status === 'DISCONTINUED' ? '#fecaca' : cand.status === 'COMPLETED' ? '#bbf7d0' : '#fde68a'}`
                                            }}>
                                                {cand.status === 'DISCONTINUED' ? 'DISCONTINUED' : cand.status === 'COMPLETED' ? 'COMPLETED' : cand.status}
                                            </span>
                                        </td>
                                        <td style={{ padding: '16px 24px', textAlign: 'right' }}>
                                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                                                <button className="btn-icon" style={{ padding: '8px', borderRadius: '10px', background: 'rgba(128,0,0,0.05)', border: 'none', color: 'var(--primary)', cursor: 'pointer' }}>
                                                    <Icon icon="solar:eye-bold" width="18" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination matching Audit Trail */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '32px', paddingTop: '24px', borderTop: '1px solid var(--border-light)' }}>
                    <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
                        Showing page <span style={{ fontWeight: 700, color: 'var(--text-main)' }}>{page}</span> of <span style={{ fontWeight: 700, color: 'var(--text-main)' }}>{totalPages || 1}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                            disabled={page === 1}
                            onClick={() => setPage(page - 1)}
                            className="btn-secondary"
                            style={{ padding: '8px 16px', opacity: page === 1 ? 0.5 : 1, cursor: page === 1 ? 'not-allowed' : 'pointer', minWidth: '100px' }}
                        >
                            Previous
                        </button>
                        {totalPages > 1 && [...Array(totalPages)].map((_, i) => (
                            <button
                                key={i + 1}
                                onClick={() => setPage(i + 1)}
                                className={page === i + 1 ? "btn-primary" : "btn-secondary"}
                                style={{ width: '40px', height: '40px', padding: 0 }}
                            >
                                {i + 1}
                            </button>
                        )).slice(Math.max(0, page - 3), Math.min(totalPages, page + 2))}
                        <button
                            disabled={page === totalPages || totalPages === 0}
                            onClick={() => setPage(page + 1)}
                            className="btn-secondary"
                            style={{ padding: '8px 16px', opacity: (page === totalPages || totalPages === 0) ? 0.5 : 1, cursor: (page === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer', minWidth: '100px' }}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>

            <CandidateDetailDrawer
                candidate={selectedCandidate}
                isOpen={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
            />

            <style sx>{`
                .table-row-hover:hover { background: #f1f5f9 !important; }
                .btn-icon:hover { color: var(--primary) !important; background: rgba(128,0,0,0.05) !important; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
};

export default Candidates;
